import { Column, ColumnDef, RowData } from '@tanstack/react-table'
import { SxProps, Theme } from '@mui/material'
import dayjs from 'dayjs'
import { StringOrTemplateHeader } from '@tanstack/table-core'

import { EMPTY_VALUE } from '../../constants.ts'
import { Nullable } from '../../types.ts'

export const commonAccessorColumn = <T extends RowData>(
  header: StringOrTemplateHeader<unknown, unknown>,
  sortable = false
): ColumnDef<T> => ({
  // @ts-expect-error its okay
  header,
  enableSorting: sortable,
  cell: (props) => props.getValue() ?? EMPTY_VALUE,
})

// в SbpPayment & AcqPayment разные поля для currency
export const moneyAccessorColumn = <
  T extends { cur?: Nullable<string>; currency?: Nullable<string>; [key: string]: any },
>(
  header: string
): ColumnDef<T> => ({
  header,
  enableSorting: false,
  cell: (props) =>
    props.getValue()
      ? ((props.getValue() as Nullable<number | string>) ?? 0)?.toLocaleString('ru-RU', {
          style: 'currency',
          currency: props.row.original.cur ?? props.row.original.currency ?? 'RUB',
        })
      : EMPTY_VALUE,
})

export const dateAccessorColumn = <T extends RowData>(header: string, withTime = false, tz?: string): ColumnDef<T> => ({
  header,
  enableSorting: false,
  cell: (props) => {
    const date = props.getValue()
    if (!date) {
      return EMPTY_VALUE
    }

    const dateTemplate = `DD.MM.YYYY${withTime ? ' HH:mm' : ''}`
    const prepareDate = dayjs(props.getValue() as number).utc(false)

    if (tz) {
      return prepareDate.tz(tz).format(dateTemplate)
    }

    return prepareDate.format(dateTemplate)
  },
})

type BooleanAccessorColumn = <T extends RowData>(header: string, locales?: [string, string]) => ColumnDef<T>

export const booleanAccessorColumn: BooleanAccessorColumn = (header: string, [positive, negative] = ['Да', 'Нет']) => ({
  header,
  enableSorting: false,
  cell: (props) => (props.getValue() ? positive : negative),
})

export const getCommonPinningStyles =
  (column: Column<any>, isHeader = false): SxProps<Theme> =>
  (theme) => {
    const isPinned = column.getIsPinned()
    // const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left')
    // const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right')
    const headerColor = isHeader && isPinned ? theme.palette.lines.grey : 'initial'

    return {
      ...theme.typography.body1,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      opacity: 1,
      backgroundColor: isPinned && !isHeader ? theme.palette.background.default : headerColor,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
      ...(isPinned && {
        '&:after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          // borderLeft: '1px solid',
          borderColor: theme.palette.divider,
        },
      }),
    }
  }
